import { Option, Period } from '@spike/model';
import { fetchPetServicesThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { showErrorThunk } from '@spike/notifications-action';
import { abandonedBookingsClientFilterOptions } from 'components/Bookings2/Abandoned/AbandonedBookings';
import { push } from 'connected-react-router';
import { useApiClientWrapper, useWaitForReduxChange } from 'hooks';
import { BookingAttempt } from 'model/BookingAttempt';
import { AppointmentMultiple, DataVaraible, Discount } from 'model/NewMultipleBooking';
import { OnlineBookingStep } from 'model/OnlineBooking';
import moment from 'moment-timezone';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TwoWaySMSPath } from 'routes/Paths';
import { RootState, store } from 'store';

interface AbandonedBookingsContextProps {
    // Data
    isLoading: Map<string, boolean>;
    bookingAttempts: Array<BookingAttempt>;

    // Pagination
    size: number;
    page: number;
    totalPages: number;
    setSize: (size: number) => void;
    setPage: (page: number) => void;

    // Search query
    query: string;
    setQuery: (query: string) => void;

    // Type filter
    type: string;
    setType: (type: string) => void;

    // Step filter
    selectedSteps: Array<OnlineBookingStep>;
    setSelectedSteps: (steps: Array<OnlineBookingStep>) => void;

    // Client filters
    clientFilterOptions: Array<Option<string>>;
    selectedClientFilters: Array<string>;
    setSelectedClientFilters: (filters: Array<string>) => void;

    // Date period
    datePeriod: Period;
    setDatePeriod: (period: Period) => void;

    // Create booking
    showCreateBooking: boolean;
    setShowCreateBooking: (show: boolean) => void;
    bookingAttempt: BookingAttempt | null;
    bookingAttemptAppointment: Array<AppointmentMultiple>;

    // Actions
    sendSmsHandler: (bookingAttempt: BookingAttempt) => void;
    openCreateBookingHandler: (bookingAttempt: BookingAttempt) => void;
    closeCreateBookingHandler: () => void;
}

export const AbandonedBookingsContext = React.createContext<AbandonedBookingsContextProps | null>(null);

export const AbandonedBookingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { waitForStateChange } = useWaitForReduxChange();

    const today = moment().startOf('day');

    const { bookingAttempts, pagination } = useSelector((state: RootState) => state.bookingAttempts);

    const { services: addOnServices } = useSelector((state: RootState) => state.addOnServices);

    const [isLoading, setIsLoading] = React.useState<Map<string, boolean>>(new Map());

    // Pagination
    const [size, setSize] = React.useState(pagination?.size ?? 10);
    const [page, setPage] = React.useState(pagination?.page ?? 1);

    // Filters
    const [type, setType] = React.useState('abandoned');
    const [query, setQuery] = React.useState<string>('');
    const [selectedSteps, setSelectedSteps] = React.useState<Array<OnlineBookingStep>>(
        Object.values(OnlineBookingStep)
    );
    const [selectedClientFilters, setSelectedClientFilters] = React.useState<Array<string>>(
        abandonedBookingsClientFilterOptions.map(option => option.id)
    );
    const [datePeriod, setDatePeriod] = React.useState<Period>({ from: today, to: today });

    // Create booking
    const [showCreateBooking, setShowCreateBooking] = React.useState<boolean>(false);

    // Booking attempt
    const [bookingAttempt, setBookingAttempt] = React.useState<BookingAttempt | null>(null);
    const [bookingAttemptAppointment, setBookingAttemptAppointment] = React.useState<Array<AppointmentMultiple>>([]);

    // Actions
    const sendSmsHandler = React.useCallback(
        (bookingAttempt: BookingAttempt) => {
            dispatch(push(`${TwoWaySMSPath}?customerId=${bookingAttempt.customerId}`));
        },
        [dispatch]
    );

    const openCreateBookingHandler = React.useCallback(
        async (attempt: BookingAttempt) => {
            const bookingItems = attempt.bookingData.bookingItems;

            // If no booking items, just open the booking directly
            if (!bookingItems || bookingItems.length === 0) {
                setShowCreateBooking(true);
                setBookingAttempt(attempt);
                return;
            }

            setIsLoading(prev => {
                const newMap = new Map(prev);
                newMap.set(attempt.id, true);
                return newMap;
            });

            try {
                const attemptAppointments: Array<AppointmentMultiple> = [];

                // Sequential fetching for each pet/service combination
                for (const item of bookingItems) {
                    const itemPetId = item.petId;
                    const bookingDate = attempt.bookingData.dateTime;
                    const attemptStaffId = attempt.bookingData.staffId;

                    if (!itemPetId || !item.serviceId || !attemptStaffId || !bookingDate) {
                        continue;
                    }

                    dispatch(fetchPetServicesThunk(apiClientWrapper, itemPetId));

                    // Wait for fetch to complete
                    await waitForStateChange(
                        state => state.newBookings.status,
                        NewBookingsStatus.FetchPetServicesSuccess
                    );

                    const petServices = store.getState().newBookings.petServices;
                    const petService = petServices.find(ps => ps.service.id === item.serviceId);

                    if (petService) {
                        attemptAppointments.push({
                            petId: itemPetId,
                            clientId: attempt.customerId,
                            createdByStaffId: attemptStaffId,
                            notes: attempt.bookingData.notes,
                            variableData: [
                                {
                                    staffId: attemptStaffId,
                                    date: bookingDate.clone(),
                                    service: {
                                        service: petService.service,
                                        price: petService.price,
                                        durationInMinutes: petService.durationInMinutes
                                    }
                                },

                                ...item.addons.reduce<Array<DataVaraible>>((addons, addon) => {
                                    const addOnService = addOnServices.find(addOn => addOn.id === addon.id);

                                    if (addOnService && attempt.bookingData.dateTime) {
                                        addons.push({
                                            addOn: addOnService,
                                            staffId: attemptStaffId,
                                            date: bookingDate.clone(),
                                            itemData: {
                                                discount: {
                                                    value: 0,
                                                    type: 'amount' as Discount['type']
                                                },
                                                quantity: addon.quantity,
                                                price: addOnService.price
                                            }
                                        });
                                    }

                                    return addons;
                                }, [])
                            ]
                        });
                    }
                }

                setBookingAttemptAppointment(attemptAppointments);
                setBookingAttempt(attempt);
                setShowCreateBooking(true);
            } catch (error) {
                dispatch(showErrorThunk('One or more services could not be fetched. Please try again.'));
                console.warn('Failed to fetch pet services:', error);
            } finally {
                setIsLoading(prev => {
                    const newMap = new Map(prev);
                    newMap.set(attempt.id, false);
                    return newMap;
                });
            }
        },
        [apiClientWrapper, dispatch, waitForStateChange]
    );

    const closeCreateBookingHandler = React.useCallback(() => {
        setShowCreateBooking(false);
        setBookingAttempt(null);
    }, []);

    return (
        <AbandonedBookingsContext.Provider
            value={{
                // Data
                bookingAttempts,
                isLoading,

                // Pagination
                size,
                page,
                totalPages: pagination?.totalPages ?? 1,
                setPage,
                setSize,
                query,
                setQuery,

                // Filters
                type,
                setType,
                selectedSteps,
                setSelectedSteps,
                clientFilterOptions: abandonedBookingsClientFilterOptions,
                selectedClientFilters,
                setSelectedClientFilters,
                datePeriod,
                setDatePeriod,

                // Create booking
                showCreateBooking,
                setShowCreateBooking,

                // Booking attempt
                bookingAttempt,
                bookingAttemptAppointment,

                // Actions
                sendSmsHandler,
                openCreateBookingHandler,
                closeCreateBookingHandler
            }}
        >
            {children}
        </AbandonedBookingsContext.Provider>
    );
};

export const useAbandonedBookings = (): AbandonedBookingsContextProps => {
    const context = React.useContext(AbandonedBookingsContext);

    if (!context) {
        throw new Error('useAbandonedBookings must be used within an AbandonedBookingsProvider');
    }

    return context;
};
